/**
 * Ranked Character Picker Modal for Settings (Hangout)
 * Opens a modal with ranked characters and writes selection back to the hidden <select>.
 */

import { getCurrentLanguage, loadTranslations } from '../../../translations.js';
import { characterIsNsfw } from '../../../utils/nsfwTags.js';
import { getNsfwEnabled } from '../../../utils/userPrefs.js';
import { filterBlockedCharacters, ensureBlockedContentLoaded } from '../../../moderation/blockedContentStore.js';
import {
    bindPickerCardMedia,
    createPickerCardMediaEl,
    fetchPickerCardMedia,
    isPrivateCharacter,
    releasePickerCardMedia,
} from '../../../rankings/services/characterCardMedia.js';

let rankMapCache = null;
let rankMapPromise = null;
let spriteCache = null;
let spritePending = null;

const toStr = (v) => (v === null || v === undefined ? '' : String(v));
const normalize = (v) => toStr(v).toLowerCase().trim();

const resolveMessagesTotalTooltip = () => {
    const lang = getCurrentLanguage() || 'en';
    const rankingsT = window.translations?.[lang]?.rankings || window.translations?.en?.rankings || {};
    return rankingsT['rankings.messagesTotalTooltip'] || 'Total number of messages';
};

const ensureRankingsTooltipTranslations = async () => {
    try {
        const lang = getCurrentLanguage();
        if (!window.translations?.[lang]?.rankings?.['rankings.messagesTotalTooltip']) {
            await loadTranslations('rankings', lang);
        }
    } catch {
        // English tooltip fallback remains
    }
};

const formatPopularity = (pop) => {
    const num = Number.parseInt(toStr(pop), 10);
    if (!Number.isFinite(num)) return '0';
    if (num >= 1_000_000) return `${Math.floor(num / 100_000) / 10}M`;
    if (num >= 1_000) return `${Math.floor(num / 100) / 10}k`;
    return String(num);
};

const getRankMap = async () => {
    if (rankMapCache) return rankMapCache;
    if (rankMapPromise) return rankMapPromise;

    rankMapPromise = (async () => {
        try {
            const { R2DataService } = await import('../../../../services/R2DataService.js');
            const { computeCharacterRanks } = await import('../../../rankings/components/rank-utils.js');
            const raw = await new R2DataService().getCharacterRankings(true);
            const ranked = computeCharacterRanks(raw || []);
            const map = new Map();
            for (const c of ranked) {
                const id = toStr(c?.id).trim();
                const rankNum = Number.parseInt(toStr(c?.rank), 10);
                if (!id) continue;
                if (!Number.isFinite(rankNum)) continue;
                map.set(id, rankNum);
            }
            rankMapCache = map;
            return map;
        } catch (error) {
            console.warn('[settings character picker] failed to load rankings; falling back to name sort', error);
            rankMapCache = new Map();
            return rankMapCache;
        } finally {
            rankMapPromise = null;
        }
    })();

    return rankMapPromise;
};

const getSpriteCaches = () => {
    if (!spriteCache) spriteCache = new Map();
    if (!spritePending) spritePending = new Map();
    return { spriteCache, spritePending };
};

const fetchSpriteUrlForCard = async (characterId, gender, { allowFallback = true } = {}) => {
    const FALLBACK_SPRITE_URL = '/assets/images/kiss.webp';
    const id = toStr(characterId).trim();
    if (!id) return allowFallback ? FALLBACK_SPRITE_URL : null;

    const g = (toStr(gender).toLowerCase() === 'male' || toStr(gender) === '1') ? 'male' : 'female';
    const key = `${id}:${g}`;
    const caches = getSpriteCaches();
    const fallback = allowFallback ? FALLBACK_SPRITE_URL : null;

    if (caches.spriteCache.has(key)) {
        return caches.spriteCache.get(key) || fallback;
    }

    if (caches.spritePending.has(key)) {
        const pending = await caches.spritePending.get(key);
        return pending || fallback;
    }

    const promise = (async () => {
        try {
            const { fetchCache } = await import('../../../utils/fetchCache.js');
            const { getDefaultStandingActionId } = await import('../../../utils/spriteFallbacks.js');
            const defaultActionId = getDefaultStandingActionId(g);
            const data = await fetchCache.fetchCharacterSprite(id, defaultActionId, 0, g, true);
            const url = toStr(data?.spriteUrl).trim();
            if (url) {
                caches.spriteCache.set(key, url);
                return url;
            }
            caches.spriteCache.set(key, null);
            return null;
        } catch (error) {
            console.warn('[settings character picker] sprite fetch failed', { id, gender: g }, error);
            caches.spriteCache.set(key, null);
            return null;
        } finally {
            caches.spritePending.delete(key);
        }
    })();

    caches.spritePending.set(key, promise);
    return (await promise) || fallback;
};

const preloadImageUrl = (url) => {
    const src = toStr(url).trim();
    if (!src) return Promise.resolve(false);

    return new Promise((resolve) => {
        const probe = new Image();
        probe.decoding = 'async';
        probe.onload = () => resolve(true);
        probe.onerror = () => resolve(false);
        probe.src = src;
    });
};

const getReadySpriteUrlForPicker = async (characterId, gender) => {
    const url = await fetchSpriteUrlForCard(characterId, gender, { allowFallback: false });
    if (!url) return null;
    const ok = await preloadImageUrl(url);
    return ok ? url : null;
};

const getReadyPickerMedia = async (item) => {
    const fetched = await fetchPickerCardMedia(item);
    if (fetched) {
        if (fetched.kind === 'image' && !String(fetched.src).startsWith('blob:')) {
            const ok = await preloadImageUrl(fetched.src);
            if (ok) return fetched;
        } else {
            return fetched;
        }
    }
    const spriteUrl = await getReadySpriteUrlForPicker(item.id, item.gender);
    if (!spriteUrl) return null;
    return { kind: 'image', src: spriteUrl, freeze: false, ext: null };
};

const buildSortedCharacters = (allCharacters, rankMap, searchTerm) => {
    const q = normalize(searchTerm);
    const nsfwAllowed = getNsfwEnabled();
    const list = (Array.isArray(allCharacters) ? allCharacters : [])
        .filter((c) => nsfwAllowed || !characterIsNsfw(c))
        .map(c => {
            const id = toStr(c?.id).trim();
            const name = toStr(c?.name).trim() || `Character ${id || '?'}`;
            const isPrivate = isPrivateCharacter(c);
            const rank = isPrivate ? null : rankMap?.get(id);
            const popularity = Number.parseInt(toStr(c?.total_messages_sent || c?.pop || 0), 10);
            const sprites = Number.parseInt(toStr(c?.sprites || 0), 10);
            const gender = c?.gender;
            const image = toStr(c?.image || c?.image_url).trim();
            return {
                ...c,
                id,
                name,
                rank: Number.isFinite(rank) ? rank : null,
                popularity: Number.isFinite(popularity) ? popularity : 0,
                sprites: Number.isFinite(sprites) ? sprites : 0,
                gender,
                private: isPrivate,
                image
            };
        })
        .filter(c => !!c.id)
        .filter(c => !q || normalize(c.name).includes(q));

    return filterBlockedCharacters(list).sort((a, b) => {
        if (a.private !== b.private) return a.private ? -1 : 1;
        const ar = a.rank ?? Number.POSITIVE_INFINITY;
        const br = b.rank ?? Number.POSITIVE_INFINITY;
        if (ar !== br) return ar - br;
        return a.name.localeCompare(b.name);
    });
};

const getUsedCharacterIdsForOtherSlots = (crewMap, excludeSlotIndex) => {
    const used = new Set();
    const numSelect = document.getElementById('num-allCharacters-settings');
    const visibleCountRaw = numSelect ? Number.parseInt(numSelect.value, 10) : 1;
    const visibleCount = Number.isFinite(visibleCountRaw) ? Math.min(Math.max(visibleCountRaw, 1), crewMap.length) : 1;

    for (let i = 0; i < visibleCount; i++) {
        if (i === excludeSlotIndex) continue;
        const selectId = crewMap[i]?.charSelect;
        const el = selectId ? document.getElementById(selectId) : null;
        const id = toStr(el?.value).trim();
        if (id) used.add(id);
    }
    return used;
};

const ensureModalShell = () => {
    const existing = document.getElementById('settings-character-picker-modal');
    if (existing) return existing;

    const modal = document.createElement('div');
    modal.id = 'settings-character-picker-modal';
    modal.className = 'modal create-container';

    const content = document.createElement('div');
    content.className = 'modal-content create-panel';

    modal.appendChild(content);
    document.body.appendChild(modal);
    return modal;
};

const openModal = (modal) => {
    if (!modal) return;
    modal.classList.add('is-open');
    requestAnimationFrame(() => modal.classList.add('show'));
};

const closeModal = (modal) => {
    if (!modal) return;
    modal.classList.remove('show');
    setTimeout(() => {
        modal.classList.remove('is-open');
    }, 300);
};

export const openRankedCharacterPickerModal = async ({ selectId, allCharacters, crewMap, slotIndex }) => {
    const selectEl = document.getElementById(selectId);
    if (!selectEl) return;

    const modal = ensureModalShell();
    const content = modal.querySelector('.modal-content');
    if (!content) return;

    await ensureRankingsTooltipTranslations();
    await ensureBlockedContentLoaded();

    const usedIds = getUsedCharacterIdsForOtherSlots(crewMap, slotIndex);
    const currentValue = toStr(selectEl.value).trim();
    // Open immediately; load rankings in the background (slow networks were making this feel like "nothing happens").
    let rankMap = new Map();
    let ranksLoaded = false;

    let searchTerm = '';
    let isClosing = false;

    const cleanupKeydown = (() => {
        let handler = null;
        return {
            set(fn) { handler = fn; },
            remove() {
                if (handler) document.removeEventListener('keydown', handler, true);
                handler = null;
            }
        };
    })();

    const handleClose = () => {
        if (isClosing) return;
        isClosing = true;
        cleanupKeydown.remove();
        releasePickerCardMedia(content);
        closeModal(modal);
    };

    const render = () => {
        releasePickerCardMedia(content);
        content.innerHTML = '';

        const header = document.createElement('div');
        header.className = 'settings-picker-header';

        const title = document.createElement('div');
        title.className = 'settings-picker-title';
        title.textContent = ranksLoaded ? 'Select a character' : 'Select a character (loading rankings...)';

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'settings-picker-close';
        closeBtn.textContent = '×';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.tabIndex = 0;
        closeBtn.addEventListener('click', handleClose);

        header.appendChild(title);
        header.appendChild(closeBtn);

        const searchWrap = document.createElement('div');
        searchWrap.className = 'settings-picker-search-wrap';

        const searchInput = document.createElement('input');
        searchInput.type = 'text';
        searchInput.className = 'settings-picker-search';
        searchInput.value = searchTerm;
        searchInput.placeholder = 'Search characters...';
        searchInput.setAttribute('aria-label', 'Search characters');
        searchInput.addEventListener('input', (e) => {
            searchTerm = e?.target?.value || '';
            render();
        });

        searchWrap.appendChild(searchInput);

        const listWrap = document.createElement('div');
        listWrap.className = 'settings-picker-list';

        const items = buildSortedCharacters(allCharacters, rankMap, searchTerm);
        const grid = document.createElement('div');
        grid.className = 'picker-grid';

        if (!items.length) {
            const empty = document.createElement('div');
            empty.className = 'settings-picker-note';
            empty.textContent = 'No results';
            listWrap.appendChild(empty);
        } else {
            const createCardEl = (item, media) => {
                const isUsedElsewhere = usedIds.has(item.id);
                const isSelected = item.id === currentValue;

                const cardBtn = document.createElement('button');
                cardBtn.type = 'button';
                cardBtn.className = 'character-card';
                cardBtn.tabIndex = 0;
                cardBtn.disabled = isUsedElsewhere;
                cardBtn.setAttribute('aria-label', `Select ${item.name}`);

                const inner = document.createElement('div');
                inner.className = 'card-inner';

                const front = document.createElement('div');
                front.className = 'card-front';

                const rankBadge = document.createElement('div');
                rankBadge.className = 'picker-badge';
                rankBadge.textContent = item.private
                    ? 'Private'
                    : (ranksLoaded
                        ? (item.rank ? `#${item.rank}` : '—')
                        : '...');

                const selectedBadge = isSelected ? document.createElement('div') : null;
                if (selectedBadge) {
                    selectedBadge.className = 'picker-selected';
                    selectedBadge.textContent = 'Selected';
                }

                if (media?.src) {
                    const mediaEl = createPickerCardMediaEl({
                        alt: item.name,
                        kind: media.kind === 'video' ? 'video' : 'image',
                    });
                    bindPickerCardMedia(mediaEl, media);
                    front.appendChild(mediaEl);
                }

                const nameEl = document.createElement('h3');
                nameEl.textContent = item.name;

                const countsEl = document.createElement('div');
                countsEl.className = 'card-counts';

                const popEl = document.createElement('div');
                popEl.className = 'message-count';
                popEl.title = resolveMessagesTotalTooltip();
                popEl.setAttribute('data-translate-attr', 'title:rankings.messagesTotalTooltip');
                popEl.innerHTML = `<i class="fas fa-comment"></i> ${formatPopularity(item.popularity)}`;

                const spritesEl = document.createElement('div');
                spritesEl.className = 'sprites-count';
                spritesEl.title = 'Number of sprites';
                spritesEl.innerHTML = `<i class="fas fa-image"></i> ${formatPopularity(item.sprites)}`;

                countsEl.appendChild(popEl);
                countsEl.appendChild(spritesEl);

                inner.appendChild(front);
                inner.appendChild(rankBadge);
                if (selectedBadge) inner.appendChild(selectedBadge);
                front.appendChild(nameEl);
                front.appendChild(countsEl);
                cardBtn.appendChild(inner);

                cardBtn.addEventListener('click', () => {
                    if (cardBtn.disabled) return;
                    if (!Array.from(selectEl.options || []).some((opt) => opt.value === item.id)) {
                        selectEl.add(new Option(item.name, item.id));
                    }
                    if (selectEl.value !== item.id) {
                        selectEl.value = item.id;
                        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    handleClose();
                });

                return cardBtn;
            };

            const loadingNote = document.createElement('div');
            loadingNote.className = 'settings-picker-note';
            loadingNote.textContent = 'Loading characters...';
            listWrap.appendChild(loadingNote);
            listWrap.appendChild(grid);

            // Progressive append: only create/append a card once its sprite is preloaded (or we definitively have no sprite).
            // This prevents any placeholder image from flashing.
            const renderId = String(Date.now()) + Math.random().toString(16).slice(2);
            listWrap.dataset.renderId = renderId;

            let nextIndex = 0;
            const batchSize = 24;

            const appendBatch = async () => {
                if (listWrap.dataset.renderId !== renderId) return;
                const batch = items.slice(nextIndex, nextIndex + batchSize);
                if (!batch.length) return;
                nextIndex += batch.length;

                const mediaList = await Promise.all(batch.map((item) => getReadyPickerMedia(item)));

                if (listWrap.dataset.renderId !== renderId) return;
                if (loadingNote.isConnected) loadingNote.remove();

                const frag = document.createDocumentFragment();
                for (let i = 0; i < batch.length; i++) {
                    frag.appendChild(createCardEl(batch[i], mediaList[i]));
                }
                grid.appendChild(frag);
            };

            const handleScrollLoadMore = () => {
                if (listWrap.dataset.renderId !== renderId) return;
                const nearBottom = (listWrap.scrollTop + listWrap.clientHeight) >= (listWrap.scrollHeight - 450);
                if (!nearBottom) return;
                appendBatch();
            };

            listWrap.addEventListener('scroll', handleScrollLoadMore, { passive: true });
            appendBatch();
        }

        content.appendChild(header);
        content.appendChild(searchWrap);
        content.appendChild(listWrap);

        // Focus the search field after render
        requestAnimationFrame(() => {
            searchInput.focus();
        });

        // Sprites are now preloaded before cards are appended to prevent any placeholder flash.
    };

    // Click outside closes
    modal.onclick = (e) => {
        if (e.target === modal) handleClose();
    };

    // Capture Escape so the settings modal's Escape handler doesn't run.
    const onKeyDownCapture = (event) => {
        const key = event.key || event.code;
        if (key === 'Escape' || key === 'Esc') {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            handleClose();
        }
    };
    cleanupKeydown.set(onKeyDownCapture);
    document.addEventListener('keydown', onKeyDownCapture, true);

    render();
    openModal(modal);

    // Load ranks asynchronously and re-render when ready.
    getRankMap()
        .then((map) => {
            rankMap = map || new Map();
            ranksLoaded = true;
            render();
        })
        .catch(() => {
            rankMap = new Map();
            ranksLoaded = true;
            render();
        });
};

export const attachRankedCharacterPickerToTrigger = ({ selectId, allCharacters, crewMap, slotIndex }) => {
    const trigger = document.getElementById(`${selectId}-search-trigger`);
    if (!trigger) return;

    const handleOpen = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        // Guard against duplicate opens from touchstart + click.
        if (trigger.dataset.pickerOpening === 'true') return;
        trigger.dataset.pickerOpening = 'true';
        await openRankedCharacterPickerModal({ selectId, allCharacters, crewMap, slotIndex });
        // Release on next tick so future opens work.
        setTimeout(() => { trigger.dataset.pickerOpening = 'false'; }, 0);
    };

    // Capture phase prevents the searchDropdown's own click/keydown handlers from firing.
    trigger.addEventListener('click', handleOpen, true);
    // Mobile reliability: intercept touch/pointer before click synthesis.
    trigger.addEventListener('touchstart', handleOpen, { capture: true, passive: false });
    trigger.addEventListener('pointerdown', handleOpen, { capture: true });
    trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') return handleOpen(e);
    }, true);
};

